import { useEffect, useState } from "react";
import { Button, FormInput, FormSelect, FormTextarea, Modal } from "../../../components/ui";
import { validateLocation } from "../validations/locationValidation";

const EMPTY_FORM = {
  name: "",
  contactName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  pincode: "",
  phone: "",
  notes: "",
};

export default function LocationModal({ isOpen, onClose, onSave, initialData }) {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!initialData) {
      setFormData(EMPTY_FORM);
      setErrors({});
      return;
    }
    setFormData({
      name: initialData.name || "",
      contactName: initialData.contactName || "",
      addressLine1: initialData.addressLine1 || initialData.address || "",
      addressLine2: initialData.addressLine2 || "",
      city: initialData.city || "",
      state: initialData.state || "",
      pincode: initialData.pincode || "",
      phone: initialData.phone || "",
      notes: initialData.notes || "",
    });
    setErrors({});
  }, [initialData, isOpen]);

  const updateField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const validateForm = () => {
    const baseErrors = validateLocation(formData);
    const nextErrors = { ...baseErrors };

    const name = formData.name.trim();
    const contactName = formData.contactName.trim();
    const city = formData.city.trim();

    if (name && name.length < 3) nextErrors.name = "Minimum 3 characters required";
    if (contactName && !/^[a-zA-Z\s.]+$/.test(contactName)) {
      nextErrors.contactName = "Only letters allowed";
    }
    if (city && city.length < 2) nextErrors.city = "Enter a valid city";

    return nextErrors;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validateForm();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    setIsSubmitting(true);
    try {
      await onSave({
        ...formData,
        name: formData.name.trim(),
        contactName: formData.contactName.trim(),
        addressLine1: formData.addressLine1.trim(),
        addressLine2: formData.addressLine2.trim(),
        city: formData.city.trim(),
        notes: formData.notes.trim(),
        address: `${formData.addressLine1}, ${formData.addressLine2 || ""}`.replace(/, $/, ""),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={initialData ? "Edit Store" : "Add Store"}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormInput
            field="name"
            label="Store Name"
            required
            value={formData.name}
            onChange={(e) => updateField("name", e.target.value)}
            placeholder="e.g. Hyderabad Head Office"
            error={errors.name}
            size="sm"
          />
          <FormInput
            field="contactName"
            label="Contact Person Name"
            required
            value={formData.contactName}
            onChange={(e) => updateField("contactName", e.target.value)}
            placeholder="Full Name"
            error={errors.contactName}
            size="sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormInput
            field="phone"
            label="Contact Phone"
            required
            type="tel"
            inputMode="numeric"
            value={formData.phone}
            onChange={(e) => updateField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
            placeholder="10 digit mobile number"
            error={errors.phone}
            size="sm"
          />
          <FormInput
            field="addressLine1"
            label="Address Line 1"
            required
            value={formData.addressLine1}
            onChange={(e) => updateField("addressLine1", e.target.value)}
            placeholder="Street address, building, etc."
            error={errors.addressLine1}
            size="sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormInput
            field="addressLine2"
            label="Address Line 2"
            value={formData.addressLine2}
            onChange={(e) => updateField("addressLine2", e.target.value)}
            placeholder="Apartment, suite, etc."
            size="sm"
          />
          <FormInput
            field="city"
            label="City"
            required
            value={formData.city}
            onChange={(e) => updateField("city", e.target.value)}
            placeholder="City"
            error={errors.city}
            size="sm"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <FormSelect
            field="state"
            label="State"
            required
            value={formData.state}
            onChange={(e) => updateField("state", e.target.value)}
            error={errors.state}
            size="sm"
          >
            <option value="">-- Select State --</option>
            <option value="Andhra Pradesh">Andhra Pradesh</option>
            <option value="Telangana">Telangana</option>
            <option value="Orissa">Orissa</option>
            <option value="Karnataka">Karnataka</option>
            <option value="Tamil Naidu">Tamil Naidu</option>
            <option value="Pondicherry">Pondicherry</option>
          </FormSelect>
          <FormInput
            field="pincode"
            label="PIN Code"
            required
            type="tel"
            inputMode="numeric"
            value={formData.pincode}
            onChange={(e) => updateField("pincode", e.target.value.replace(/\D/g, "").slice(0, 6))}
            placeholder="6 digit PIN code"
            error={errors.pincode}
            size="sm"
          />
        </div>

        <FormTextarea
          field="notes"
          label="Administrative Notes"
          value={formData.notes}
          onChange={(e) => updateField("notes", e.target.value)}
          placeholder="Add any specific notes for this office..."
          rows={2}
        />

        <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
          <Button type="button" variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="sm" loading={isSubmitting}>
            Save Location
          </Button>
        </div>
      </form>
    </Modal>
  );
}




